import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, safeLocalGet, safeLocalSet, safeLocalRemove } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AUTH } from "@/constants/testIds";
import { Toaster } from "sonner";

const REMEMBER_KEY = "cas_login_remember";
const REMEMBER_EMAIL_KEY = "cas_login_email";

function readRememberPrefs() {
  const remember = safeLocalGet(REMEMBER_KEY, "1") !== "0";
  const email = remember ? (safeLocalGet(REMEMBER_EMAIL_KEY, "") || "") : "";
  return { remember, email };
}

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const prefs = readRememberPrefs();
  const [email, setEmail] = useState(prefs.email);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(prefs.remember);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const safeNext = (() => {
    const raw = searchParams.get("next") || "";
    if (raw.startsWith("/") && !raw.startsWith("//")) return raw;
    return "/";
  })();

  useEffect(() => {
    safeLocalSet(REMEMBER_KEY, remember ? "1" : "0");
    if (!remember) safeLocalRemove(REMEMBER_EMAIL_KEY);
  }, [remember]);

  if (user && user !== false && user !== null) {
    return <Navigate to={safeNext} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password, remember);
      if (remember) {
        safeLocalSet(REMEMBER_KEY, "1");
        safeLocalSet(REMEMBER_EMAIL_KEY, email.trim());
      } else {
        safeLocalSet(REMEMBER_KEY, "0");
        safeLocalRemove(REMEMBER_EMAIL_KEY);
      }
      toast.success("Welcome back");
      nav(safeNext, { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="hidden lg:flex relative overflow-hidden items-center justify-center p-12 border-r border-white/[0.06]">
        <div className="absolute inset-0" aria-hidden="true">
          <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, #8B5CF6 0%, transparent 60%)" }} />
          <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, #06B6D4 0%, transparent 60%)" }} />
        </div>
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs text-white/70 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
            Enterprise Admin Panel
          </div>
          <h1 className="text-5xl font-semibold tracking-tight text-white leading-[1.1]">
            The <span className="gradient-text">command center</span> for CAS Media Solutions.
          </h1>
          <p className="mt-5 text-white/60 text-base leading-relaxed">
            Content, media, leads, SEO, analytics, and access control — engineered as one premium surface.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-3">
            {["19 Modules", "10 Roles", "Real-time"].map((t) => (
              <div key={t} className="glass rounded-lg py-3 px-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-white/40">Includes</div>
                <div className="text-sm text-white mt-1 font-medium">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm glass-strong rounded-2xl p-8 relative">
          <div className="flex items-center gap-2 mb-8">
            <img src="/cas-logo.png" alt="CAS Media" className="h-9 w-9 object-contain rounded-lg" />
            <div>
              <div className="text-sm font-semibold text-white">CAS Media</div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Sign in</div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-white tracking-tight">Welcome back</h2>
          <p className="mt-1 text-sm text-white/50">Enter your credentials to continue</p>

          <div className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-white/50">Email</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                data-testid={AUTH.loginEmail}
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@casmedia.co"
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-white/50">Password</Label>
              <div className="relative">
                <Input
                  id="password" type={showPw ? "text" : "password"} required autoComplete="current-password"
                  data-testid={AUTH.loginPassword}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-11 pr-11"
                />
                <button
                  type="button"
                  data-testid={AUTH.loginPasswordToggle}
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-1.5 top-1/2 z-10 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  aria-pressed={showPw}
                >
                  {showPw ? <Icons.EyeOff className="w-4 h-4" /> : <Icons.Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-0.5">
              <Checkbox
                id="remember"
                data-testid={AUTH.loginRemember}
                checked={remember}
                onCheckedChange={(v) => setRemember(v === true)}
                className="border-white/30 data-[state=checked]:bg-[#8B5CF6] data-[state=checked]:border-[#8B5CF6] data-[state=checked]:text-white"
              />
              <Label htmlFor="remember" className="text-sm font-normal text-white/60 cursor-pointer">
                Remember me
              </Label>
            </div>

            {error && (
              <div data-testid={AUTH.loginError} className="text-xs px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-300">
                {error}
              </div>
            )}

            <Button
              data-testid={AUTH.loginSubmit}
              type="submit" disabled={busy}
              className="w-full btn-gradient text-white border-0 h-11"
            >
              {busy ? "Signing in…" : "Sign in"}
              {!busy && <Icons.ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>

          <div className="mt-6 text-center text-xs text-white/40">
            Protected by enterprise-grade encryption
          </div>
        </form>
      </div>
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "rgba(20,20,42,0.92)", color: "#fff", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(20px)" }
      }} />
    </div>
  );
}
